<template>
  <section id="map-preview" class="page-section asset-preview-section selection-section" data-no-parallax>
    <div class="section-header">
      <h3>Shape the interaction view</h3>
      <p>Pick the map marker and keep the interaction button styling in step with the AR flow.</p>
    </div>

    <div class="phone-preview-shell">
      <div class="phone-preview">
        <div class="phone-speaker"></div>
        <div class="phone-screen phone-screen--asset" :style="phoneScreenStyle">
          <img :src="mapSceneImage" alt="Map scene preview" class="scene-image" />
          <button
            type="button"
            :class="['tap-target', 'logo-target', { 'preview-target--configured': hasConfiguredLogo }]"
            :disabled="!hasTemplateSelection"
            aria-label="Campaign logo preview"
            @click="openLogoActionModal"
          >
            <svg class="marching-ants marching-ants--logo" viewBox="0 0 220 88" preserveAspectRatio="none" aria-hidden="true">
              <rect x="1" y="1" width="218" height="86" rx="14" ry="14" pathLength="100" />
            </svg>
            <img :src="logoPreviewUrl" alt="Campaign logo preview" class="preview-logo" />
          </button>
          <button
            type="button"
            :class="['tap-target', 'map-marker-container', { 'preview-target--configured': hasConfiguredMapMarker }]"
            :disabled="!hasTemplateSelection"
            aria-label="Choose a map marker"
            @click="openAssetActionModal"
          >
            <img
              v-if="mapMarkerPreviewUrl"
              :src="mapMarkerPreviewUrl"
              alt="Selected map marker"
              class="map-marker-image"
            />
            <div v-else class="asset-placeholder">
              <svg class="marching-ants marching-ants--panel" viewBox="0 0 220 260" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1" y="1" width="218" height="258" rx="18" ry="18" pathLength="100" />
              </svg>
              Tap to choose a map marker
            </div>
          </button>
          <button
            type="button"
            :class="['tap-target', 'action-button-target', { 'preview-target--configured': hasCustomizedActionButton }]"
            :disabled="!hasTemplateSelection"
            aria-label="Customize interaction button"
            @click="openButtonStyleModal"
          >
            <svg class="marching-ants marching-ants--pill" viewBox="0 0 220 56" preserveAspectRatio="none" aria-hidden="true">
              <rect x="1" y="1" width="218" height="54" rx="27" ry="27" pathLength="100" />
            </svg>
            <span class="preview-action-button" :style="previewButtonStyle">
              {{ buttonConfig?.label || 'Style interaction button' }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="isAssetActionModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeAssetActionModal"
      >
        <div class="asset-action-modal" role="dialog" aria-modal="true" aria-label="Map marker actions">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close map marker actions"
              @click="closeAssetActionModal"
            >
              ×
            </button>
            <h3>Map Marker</h3>
          </div>
          <div class="modal-body">
            <p>Choose how to set your map marker.</p>
            <div class="modal-actions">
              <button type="button" class="action-button" @click="handleAction('upload')">
                Upload your own
              </button>
              <button type="button" class="action-button" @click="handleAction('browse')">
                Browse defaults
              </button>
              <button
                v-if="hasMapMarker"
                type="button"
                class="action-button action-button--danger"
                @click="handleAction('clear')"
              >
                Remove marker
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <teleport to="body">
      <div
        v-if="isLogoActionModalOpen"
        class="asset-action-modal-overlay"
        @click.self="closeLogoActionModal"
      >
        <div class="asset-action-modal" role="dialog" aria-modal="true" aria-label="Logo actions">
          <div class="modal-header">
            <button
              type="button"
              class="modal-close-button"
              aria-label="Close logo actions"
              @click="closeLogoActionModal"
            >
              ×
            </button>
            <h3>Campaign logo</h3>
          </div>
          <div class="modal-body">
            <p>Choose how to set your campaign logo.</p>
            <div class="modal-actions">
              <button type="button" class="action-button" @click="handleLogoAction('upload')">
                Upload your own
              </button>
              <button type="button" class="action-button" @click="handleLogoAction('browse')">
                Browse defaults
              </button>
              <button
                v-if="hasLogo"
                type="button"
                class="action-button action-button--danger"
                @click="handleLogoAction('clear')"
              >
                Remove logo
              </button>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <ButtonStyleModal
      :show="isButtonStyleModalOpen"
      title="Interaction Button"
      description="Style the main action button shown on the interaction screen."
      :config="buttonConfig"
      :fallback-config="fallbackButtonConfig"
      :fallback-background-color="brandColors.secondary"
      :fallback-text-color="brandColors.buttonText"
      :preview-background-color="brandColors.background"
      :preview-background-image-url="backgroundPreviewUrl"
      @close="closeButtonStyleModal"
      @save="saveButtonStyle"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch, type StyleValue } from 'vue'
import { useUIStore } from '@/store/ui.store'
import mapSceneImage from '@/assets/map-image.png'
import ButtonStyleModal from '@/components/ButtonStyleModal.vue'
import type { StyledButtonConfig } from '@/types/form.types'
import { DEFAULT_BUTTON_CONFIGS } from '@/utils/buttonStyles'
import { isButtonConfigCustomized } from '@/utils/previewCustomization'

type TemplateConfigLike = { experienceType?: string | null } | null | undefined

type AssetModalAction = 'upload' | 'browse' | 'clear'
const props = defineProps<{
  templateConfig?: TemplateConfigLike
  mapMarkerPreviewUrl?: string | null
  logoPreviewUrl?: string | null
  backgroundPreviewUrl?: string | null
  brandColors: {
    background: string
    header: string
    secondary: string
    buttonText: string
  }
  buttonConfig?: StyledButtonConfig | null
  previewButtonStyle: StyleValue
}>()

const emit = defineEmits<{
  (e: 'open-upload-modal', payload: { type: 'mapMarker' | 'logo' }): void
  (e: 'open-asset-modal', payload: { type: 'mapMarker' | 'logo' }): void
  (e: 'clear-asset', type: 'mapMarker' | 'logo'): void
  (e: 'update-button-config', value: StyledButtonConfig): void
}>()

const uiStore = useUIStore()
const isAssetActionModalOpen = ref(false)
const isLogoActionModalOpen = ref(false)
const isButtonStyleModalOpen = ref(false)
const fallbackButtonConfig = DEFAULT_BUTTON_CONFIGS.interaction

const hasTemplateSelection = computed(() => !!props.templateConfig?.experienceType)
const hasMapMarker = computed(() => !!props.mapMarkerPreviewUrl)
const hasLogo = computed(() => !!props.logoPreviewUrl)
const hasConfiguredLogo = computed(() => Boolean(props.logoPreviewUrl?.trim()))
const hasConfiguredMapMarker = computed(() => hasMapMarker.value)
const hasCustomizedActionButton = computed(() => isButtonConfigCustomized('interaction', props.buttonConfig))
const logoPreviewUrl = computed(() => props.logoPreviewUrl || '')
const phoneScreenStyle = computed(() => ({
  backgroundColor: props.brandColors.background,
  backgroundImage: props.backgroundPreviewUrl ? `url('${props.backgroundPreviewUrl}')` : undefined,
  backgroundSize: props.backgroundPreviewUrl ? 'cover' : undefined,
  backgroundPosition: props.backgroundPreviewUrl ? 'center' : undefined,
  backgroundRepeat: props.backgroundPreviewUrl ? 'no-repeat' : undefined,
}))

const openAssetActionModal = () => {
  if (!hasTemplateSelection.value) return
  isAssetActionModalOpen.value = true
}

const closeAssetActionModal = () => {
  isAssetActionModalOpen.value = false
}

const openLogoActionModal = () => {
  if (!hasTemplateSelection.value) return
  isLogoActionModalOpen.value = true
}

const closeLogoActionModal = () => {
  isLogoActionModalOpen.value = false
}

const openButtonStyleModal = () => {
  if (!hasTemplateSelection.value) return
  isButtonStyleModalOpen.value = true
}

const closeButtonStyleModal = () => {
  isButtonStyleModalOpen.value = false
}

const saveButtonStyle = (value: StyledButtonConfig) => {
  emit('update-button-config', value)
  closeButtonStyleModal()
}

const handleAction = (action: AssetModalAction) => {
  if (action === 'upload') {
    emit('open-upload-modal', { type: 'mapMarker' })
  } else if (action === 'browse') {
    emit('open-asset-modal', { type: 'mapMarker' })
  } else {
    emit('clear-asset', 'mapMarker')
  }
  closeAssetActionModal()
}

const handleLogoAction = (action: AssetModalAction) => {
  if (action === 'upload') {
    emit('open-upload-modal', { type: 'logo' })
  } else if (action === 'browse') {
    emit('open-asset-modal', { type: 'logo' })
  } else {
    emit('clear-asset', 'logo')
  }
  closeLogoActionModal()
}

watch(() => isAssetActionModalOpen.value || isLogoActionModalOpen.value || isButtonStyleModalOpen.value, (isOpen) => {
  if (isOpen) {
    uiStore.openModal()
  } else {
    uiStore.closeModal()
  }
})

onBeforeUnmount(() => {
  if (isAssetActionModalOpen.value || isLogoActionModalOpen.value || isButtonStyleModalOpen.value) {
    uiStore.closeModal()
  }
})
</script>

<style scoped>
.asset-preview-section {
  justify-content: flex-start;
  align-items: stretch;
  padding-top: var(--section-padding-block-start);
}

.section-header {
  text-align: center;
  margin: 0 auto;
  width: min(100%, var(--base-layout-section-max-width));
}

.section-header h3 {
  font-size: var(--base-layout-section-heading-h3-size);
  color: var(--color-heading);
}

.section-header p {
  color: var(--color-text-muted);
  margin-top: 0.35rem;
}

.phone-preview-shell {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: var(--campaign-preview-phone-shell-margin-top);
  padding-bottom: var(--campaign-preview-phone-shell-padding-bottom);
}

.phone-preview {
  width: var(--campaign-preview-phone-width);
  max-width: 100%;
  background: #12162a;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--campaign-preview-phone-radius);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.35);
  padding: var(--campaign-preview-phone-padding);
}

.phone-speaker {
  width: 34%;
  height: var(--campaign-preview-phone-speaker-height);
  margin: 0 auto var(--campaign-preview-phone-speaker-margin-bottom);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
}

.phone-screen {
  --preview-zone-border: rgba(122, 230, 177, 0.95);
  --preview-zone-border-soft: rgba(122, 230, 177, 0.28);
  --preview-top-band: 15%;
  --preview-bottom-band: 15%;
}

.phone-screen--asset {
  --asset-screen-inline-inset: var(--campaign-preview-phone-screen-padding-inline);
  --asset-screen-top-inset: var(--campaign-preview-phone-screen-padding-block);
  --asset-screen-bottom-inset: var(--campaign-preview-phone-screen-padding-block);
  --asset-screen-gap: var(--campaign-preview-phone-gap);
  border-radius: var(--campaign-preview-phone-screen-radius);
  height: var(--campaign-preview-phone-screen-height);
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
  position: relative;
  padding: 0;
  width: 100%;
  background: #111827;
}

.scene-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.tap-target {
  position: absolute;
  z-index: 2;
  border: none;
  background: transparent;
  overflow: hidden;
}

.marching-ants {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.marching-ants rect {
  fill: none;
  stroke: var(--preview-zone-border);
  stroke-width: 1.6;
  stroke-dasharray: 2.2 2.2;
  stroke-linecap: round;
  vector-effect: non-scaling-stroke;
  animation: marching-ants-offset 1.2s linear infinite;
}

.logo-target {
  top: var(--asset-screen-top-inset);
  left: 50%;
  transform: translateX(-50%);
  width: var(--campaign-preview-phone-logo-width);
  height: var(--campaign-preview-phone-logo-height);
  min-height: var(--campaign-preview-phone-logo-height);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--campaign-preview-phone-logo-padding);
  border-radius: 14px;
}

.preview-logo {
  width: auto;
  height: auto;
  max-width: var(--campaign-preview-phone-logo-max-size);
  max-height: var(--campaign-preview-phone-logo-max-size);
  object-fit: contain;
}

.map-marker-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - (var(--asset-screen-inline-inset) * 2));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.9rem;
  border-radius: 1.1rem;
  padding: 1rem;
  overflow: visible;
  background: none;
  border: none;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
}

.interaction-stage-label {
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  background: rgba(15, 23, 42, 0.68);
  color: #e2e8f0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.action-button-target {
  left: 50%;
  right: auto;
  bottom: var(--asset-screen-bottom-inset);
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--campaign-preview-phone-button-width);
  max-width: none;
  box-sizing: border-box;
  padding: var(--campaign-preview-phone-button-shell-padding);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  z-index: 3;
  border-radius: 999px;
  overflow: visible;
}

.preview-action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--campaign-preview-phone-button-min-height);
  padding: var(--campaign-preview-phone-button-padding-block) var(--campaign-preview-phone-button-padding-inline);
  border-radius: inherit;
  box-sizing: border-box;
}

.action-button-target .marching-ants {
  inset: 1px;
}

@keyframes marching-ants-offset {
  to {
    stroke-dashoffset: -8.8;
  }
}


.map-marker-image {
  width: min(102px, 28vw);
  max-height: 100px;
  object-fit: contain;
  filter: drop-shadow(0 10px 14px rgba(2, 6, 23, 0.45));
}

.asset-placeholder {
  position: relative;
  font-size: 0.9rem;
  color: #fff;
  text-align: center;
  padding: 1.4rem 1.2rem;
  border-radius: 1.1rem;
  background: rgba(10, 17, 36, 0.65);
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
}

.asset-placeholder .marching-ants {
  inset: -1px;
  width: calc(100% + 2px);
  height: calc(100% + 2px);
}

.asset-action-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: calc(var(--base-layout-modal-z-index) + 30);
  background: rgba(8, 12, 20, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.asset-action-modal {
  width: min(420px, 100%);
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 1rem;
  overflow: hidden;
}

.modal-header {
  padding: 0.95rem 1rem;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
  position: relative;
}

.modal-header h3 {
  margin: 0;
  color: #f8fafc;
  text-align: center;
}

.modal-body {
  padding: 1rem;
}

.modal-body p {
  margin: 0 0 0.8rem;
  color: #cbd5e1;
}

.modal-actions {
  display: grid;
  gap: 0.6rem;
}

.action-button {
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 0.7rem;
  background: rgba(15, 23, 42, 0.9);
  color: #e2e8f0;
  padding: 0.65rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.action-button:hover {
  border-color: rgba(122, 230, 177, 0.9);
}

.action-button--danger {
  color: #fda4af;
  border-color: rgba(244, 63, 94, 0.5);
}

@media (max-width: 768px) {
  .map-marker-image {
    width: min(90px, 26vw);
    max-height: 88px;
  }

  .asset-placeholder {
    font-size: 0.82rem;
    padding: 1.1rem 0.9rem;
  }

  .interaction-stage-label {
    font-size: 0.66rem;
  }

  .asset-action-modal-overlay {
    align-items: flex-start;
    padding: var(--mobile-modal-overlay-top-padding) var(--mobile-modal-overlay-side-padding) var(--mobile-modal-overlay-bottom-padding);
  }
}

@media (max-width: 420px) {
  .section-header {
    width: min(100%, 18rem);
  }

  .section-header h3 {
    font-size: clamp(1.18rem, 5.2vw, 1.4rem);
    line-height: 1.15;
  }

  .section-header p {
    margin-top: 0.22rem;
    font-size: 0.82rem;
    line-height: 1.3;
  }

  .map-marker-container {
    gap: 0.55rem;
    padding: 0.7rem;
  }

  .asset-placeholder {
    font-size: 0.7rem;
    padding: 0.72rem 0.58rem;
  }

  .action-button-target {
    width: min(90%, 176px);
    font-size: 0.72rem;
  }

  .preview-action-button {
    min-height: var(--campaign-preview-phone-button-min-height);
    padding: var(--campaign-preview-phone-button-padding-block) var(--campaign-preview-phone-button-padding-inline);
    font-size: 0.64rem;
    line-height: 1.05;
  }
}
</style>
